import React, { useState, useEffect } from 'react';
import { useJazyk } from '../context/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LotusLogo } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';

import { API } from '../lib/api';

const ResetPasswordPage = () => {
  const { t } = useJazyk();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const error = searchParams.get('error');

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const passwordRules = [
    { test: (p) => p.length >= 8, label: t('reg.pozadavek8') },
    { test: (p) => /[A-Z]/.test(p), label: t('reg.pozadavekVelke') },
    { test: (p) => /[0-9]/.test(p), label: t('reg.pozadavekCislo') },
    { test: (p) => /[^a-zA-Z0-9]/.test(p), label: t('reg.pozadavekZnak') },
  ];

  const validatePassword = (pw) => {
    if (pw.length < 8) return t('reg.hesloKratke');
    if (!/[A-Z]/.test(pw)) return t('reg.hesloVelke');
    if (!/[0-9]/.test(pw)) return t('reg.hesloCislo');
    if (!/[^a-zA-Z0-9]/.test(pw)) return t('reg.hesloZnak');
    return null;
  };

  useEffect(() => {
    if (error === 'reset-expired') {
      toast.error(t('reset.odkazVyprsel'));
      navigate('/auth');
      return;
    }
    if (!token) {
      toast.error(t('reset.neplatnyOdkaz'));
      navigate('/auth');
    }
  }, [token, error, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pwErr = validatePassword(password);
    if (pwErr) { toast.error(pwErr); return; }
    if (password !== passwordConfirm) { toast.error(t('reset.neshoda')); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('reset.chyba'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4 bg-bloom-bg" data-testid="reset-password-page">
      {/* Trans flag bar */}
      <div className="pride-bar fixed top-0 left-0 right-0 z-50" />

      <Card className="w-full max-w-md bg-white border-border/50 shadow-lg">
        <CardHeader className="text-center pb-4">
          <div className="flex flex-col items-center gap-0">
            <LotusLogo size={72} />
            <CardTitle className="font-serif text-3xl text-bloom-violet">Bloom</CardTitle>
          </div>
          <CardDescription className="text-bloom-sub">{t('reset.titulek')}</CardDescription>
        </CardHeader>

        <CardContent>
          {success ? (
            <div className="text-center space-y-4 py-4">
              <div className="w-16 h-16 rounded-full bg-bloom-mint/10 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-bloom-mint" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-bloom-text">Heslo obnoveno!</h3>
              <p className="text-bloom-sub text-sm">{t('reset.hotovo')}</p>
              <Button
                onClick={() => navigate('/auth')}
                className="w-full bg-bloom-violet text-white font-semibold"
                data-testid="goto-login-btn"
              >
                {t('reset.prejitNaPrihlaseni')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Trans flag accent */}
              <div className="rounded-lg overflow-hidden border border-border/50">
                <div className="h-1 bg-gradient-to-r from-[#5BCEFA] via-[#F5A9B8] to-[#5BCEFA]" />
                <div className="p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-bloom-violet mt-0.5 shrink-0" />
                  <p className="text-xs text-bloom-sub">{t('reset.popis')}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-bloom-text">{t('reset.noveHeslo')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50" />
                  <Input
                    type={showPass ? 'text' : 'password'}
                    placeholder={t('reset.noveHesloPriklad')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-bloom-sub/50 hover:text-bloom-violet"
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="password-rules">
                    {passwordRules.map((rule, i) => (
                      <div key={i} className={`flex items-center gap-1.5 text-xs ${rule.test(password) ? 'text-bloom-mint' : 'text-bloom-sub/60'}`}>
                        {rule.test(password) ? <CheckCircle className="w-3 h-3" /> : <div className="w-3 h-3 rounded-full border border-current" />}
                        {rule.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-bloom-text">{t('reset.potvrdit')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub/50" />
                  <Input
                    type={showPass ? 'text' : 'password'}
                    placeholder="Zadejte heslo znovu"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {/* Poctivé varování: obnovou hesla se klíč ke zprávám přebalit nedá,

                  protože staré heslo tu nikdo nezná. */}

              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200/70 rounded-lg px-3 py-2.5 leading-relaxed">

                {t('reset.sifrovaniVarovani')}

              </p>

              <Button
                type="submit"
                className="w-full bg-bloom-violet text-white font-semibold"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? t('reset.ukladam') : t('reset.nastavit')}
              </Button>

              <button
                type="button"
                onClick={() => navigate('/auth')}
                className="text-sm text-bloom-sub hover:text-bloom-violet w-full text-center"
              >
                {t('auth.zpetNaPrihlaseni')}
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
